import { useEffect, useId, useState, type FormEvent } from "react";
import { useEnvironments, useSetNodeEnvironment } from "../../api/queries";
import type { Host } from "../../api/types";
import { Button } from "../../components/Button";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface ChangeEnvironmentModalProps {
  /** The machine to re-scope, or `null` while the dialog is closed — mirrors
   * `AdoptContainerModal`'s "the row IS the open state" pattern rather than a
   * separate boolean plus a stale-row risk. */
  host: Host | null;
  onClose: () => void;
}

/**
 * "Change environment…" (IA slice 2, docs/product/ui-information-architecture.md
 * §3 — the move-node control whose total absence forced a DevTools snippet
 * during the canary dogfood). Admin-only server-side AND session-only (a
 * machine credential may never re-scope itself) — this dialog is only ever
 * mounted from `HostDetailPage` behind the same `session?.role === "admin"`
 * gate that hides the button, so it never fires a request an opener could
 * only get a 403 from (S-8: the server enforces the real decision regardless).
 *
 * Surfaces the two failure modes the server documents explicitly rather than
 * a generic error: a 409 (ADR-0034 — a local machine refused into a
 * production-class environment) and a 404 (the target environment vanished
 * out from under the picker, e.g. deleted in another tab). A move that
 * widens the machine's blast radius INTO a production-class environment gets
 * a distinct, non-alarming confirmation note rather than the same toast as
 * an ordinary move.
 */
export function ChangeEnvironmentModal({ host, onClose }: ChangeEnvironmentModalProps) {
  const open = host !== null;
  const { pushToast } = useToast();
  const { data: environmentsPage, isPending: environmentsPending } = useEnvironments({ limit: 100 }, { enabled: open });
  const mutation = useSetNodeEnvironment();
  const selectId = useId();

  const [selectedId, setSelectedId] = useState("");

  // Reseed the picker to the machine's current environment every time the
  // dialog opens for a (possibly different) host — never carries a stale
  // selection from the last machine into this one.
  useEffect(() => {
    if (open) {
      setSelectedId(host.environmentId);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, host?.id]);

  const environments = environmentsPage?.items ?? [];
  const target = environments.find((e) => e.id === selectedId);
  const movingIntoProduction = target?.class === "production" && host?.environmentId !== target.id;

  function handleClose() {
    if (mutation.isPending) return;
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!host || !selectedId || selectedId === host.environmentId) {
      onClose();
      return;
    }
    mutation.mutate(
      { nodeId: host.id, input: { environmentId: selectedId } },
      {
        onSuccess: (updated) => {
          if (updated.environmentId && environments.find((e) => e.id === updated.environmentId)?.class === "production") {
            pushToast({
              title: `Moved to "${updated.environmentName}".`,
              description: "This machine is now production-protected — its blast radius just widened.",
              tone: "success",
            });
          } else {
            pushToast({ title: `Moved to "${updated.environmentName}".`, tone: "success" });
          }
          onClose();
        },
        onError: (err) => {
          // err is already typed `ApiError` (useSetNodeEnvironment's mutation
          // error type) — switch on `.status` directly rather than
          // `isConflict`/`isNotFound` (those type-guard against `unknown` for
          // callers that don't already know the type; here they'd narrow the
          // "neither" branch to `never`).
          if (err.status === 409) {
            // ADR-0034: the server's own message already explains the refusal
            // in plain language — render it verbatim rather than a generic
            // alarm (matches EnvironmentDetailPage's 409 handling).
            pushToast({ title: "Can't move this machine", description: err.message, tone: "error" });
            return;
          }
          if (err.status === 404) {
            pushToast({
              title: "That environment is no longer available",
              description: "It may have just been deleted. Pick another one.",
              tone: "error",
            });
            return;
          }
          pushToast({
            title: "Couldn't change environment",
            description: err.message || "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  if (!host) return null;

  return (
    <Modal open={open} title="Change environment" onClose={handleClose} initialFocusId={selectId}>
      <p className="form-field__hint">
        Moving <strong>{host.name}</strong> re-scopes everything on it — the environment's policy, and which
        env-scoped credentials can reach it. Local machines can never move into a production-class environment
        (ADR-0034).
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={selectId}>
          <span>Target environment</span>
          <select
            id={selectId}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={environmentsPending || mutation.isPending}
            required
          >
            {environments.length === 0 && <option value="">No environments yet</option>}
            {environments.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name} ({env.class})
              </option>
            ))}
          </select>
        </label>

        {target && (
          <p className="form-field__hint">
            <EnvironmentBadge name={target.name} envClass={target.class} />
          </p>
        )}

        {movingIntoProduction && (
          <p className="form-field__hint" role="status">
            This widens {host.name}'s blast radius into production.
          </p>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={environments.length === 0 || mutation.isPending || !selectedId}
          >
            {mutation.isPending ? "Moving…" : "Move machine"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
